import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  CalendarDaysIcon,
  ExternalLinkIcon,
  FileTextIcon,
  Loader2,
  PresentationIcon,
} from "lucide-react";
import { useState, useEffect } from "react";

export const Route = createFileRoute("/dashboard/presentation/$id/view")({
  component: PresentationViewer,
});

function PDFViewer({ url }: { url: string }) {
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const checkPdfUrl = async () => {
      try {
        const response = await fetch(url, { method: "HEAD" });
        if (!response.ok) {
          setLoadError(true);
        }
      } catch (error) {
        console.error("Error checking PDF URL:", error);
        setLoadError(true);
      }
    };

    checkPdfUrl();
  }, [url]);

  if (loadError) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 rounded-lg border-4 p-8">
        <FileTextIcon className="h-16 w-16 text-gray-400" />
        <h3 className="text-xl font-medium">Unable to load presentation PDF</h3>
        <p className="text-sm text-gray-500">
          The PDF file might not be available yet or the link may be incorrect.
        </p>
        <a
          href={url}
          target="_blank"
          className="flex flex-row items-center gap-2 text-blue-600 underline"
        >
          <span>Try direct link</span> <ExternalLinkIcon className="size-4" />
        </a>
      </div>
    );
  }

  return (
    <object
      data={url}
      type="application/pdf"
      width="100%"
      height="100%"
      className="size-full rounded-lg border-4"
    >
      <div className="flex flex-col items-center gap-2 text-gray-700">
        <p>Error: Unable to display PDF file.</p>
        <a
          href={url}
          target="_blank"
          className="flex flex-row items-center gap-2 text-blue-600 underline"
        >
          <span>Download</span> <ExternalLinkIcon className="size-4" />
        </a>
      </div>
    </object>
  );
}

function formatDate(date: Date) {
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) return day + "th";
    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  };

  return `${getOrdinalSuffix(day)} ${month} ${year}`;
}

function PresentationViewer() {
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [presentationData, setPresentationData] = useState({
    pdfUrl: "",
    pptxUrl: "",
    name: "My Presentation",
  });
  useEffect(() => {
    const loadPresentation = async () => {
      if (!id) return;

      try {
        console.log("Loading presentation with ID:", id);

        const baseStorageUrl =
          "https://storage.googleapis.com/edunova-455712.firebasestorage.app";
        const presentationUrls = [
          {
            pdf: `${baseStorageUrl}/presentations/pdf/presentation-${id}.pdf`,
            pptx: `${baseStorageUrl}/presentations/pdf/presentation-${id}.pptx`,
          },
        ];

        if (!presentationUrls || presentationUrls.length === 0) {
          console.error("No presentation URLs found for ID:", id);
          setPresentationData({
            pdfUrl: presentationUrls[0].pdf,
            pptxUrl: presentationUrls[0].pptx,
            name: `Presentation ${id}`,
          });
        }
      } catch (error) {
        console.error("Error loading presentation:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPresentation();
  }, [id]);

  const handleBackNavigation = () => {
    navigate({ to: "/dashboard/presentation" });
  };
  if (isLoading) {
    return (
      <div className="flex h-screen flex-col bg-white">
        <header className="bg-background text-white">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="text-secondary-foreground hover:bg-secondary hover:text-white"
                onClick={handleBackNavigation}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-semibold text-white">
                Presentation {id}
              </h1>
            </div>
          </div>
        </header>
        <div className="flex-1 overflow-hidden bg-background p-4">
          <div className="flex h-full flex-col gap-4 rounded-lg bg-white p-4 shadow-md">
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <Loader2 className="h-12 w-12 animate-spin" />
              <h2 className="text-2xl font-semibold">
                Loading presentation...
              </h2>
              <p className="text-center text-sm text-gray-500">
                Trying to load presentation with ID: {id}
                <br />
                If this takes too long, the presentation might still be
                processing.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="bg-background text-white">
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="text-secondary-foreground hover:bg-secondary hover:text-white"
              onClick={handleBackNavigation}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold text-white">Presentation</h1>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-hidden bg-background p-4">
        <Card className="flex size-full flex-col gap-2 px-2">
          <CardHeader className="flex flex-row items-start justify-between">
            <CardTitle
              className="text-4xl font-bold"
              title={presentationData.name || ""}
            >
              <div className="flex items-center gap-2">
                <span>
                  {presentationData.name && presentationData.name.length > 30
                    ? presentationData.name.slice(0, 30) + "..."
                    : presentationData.name}
                </span>
              </div>
            </CardTitle>
            <div className="flex items-center gap-1 text-muted">
              <CalendarDaysIcon className="size-4" />
              <p className="text-sm">{formatDate(new Date())}</p>
            </div>
          </CardHeader>

          <CardDescription className="flex h-3/4 flex-col gap-2 px-6">
            <PDFViewer url={presentationData.pdfUrl} />
          </CardDescription>
          <CardFooter className="justify-center gap-4">
            <a
              href={presentationData.pptxUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="secondary" className="flex gap-2 rounded-lg">
                <PresentationIcon className="size-4" />
                <span>Download PPTX</span>
              </Button>
            </a>
            <a
              href={presentationData.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="secondary" className="flex gap-2 rounded-lg">
                <FileTextIcon className="size-4" />
                <span>Download PDF</span>
              </Button>
            </a>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
